import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../client';
import type { UserUserIO } from '../../generated/fresh-guard-api';
import { queryKeys } from '../query-keys';

export function useUserUpdateOptimistic() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (input: { userUserIO: UserUserIO }) => {
      const res = await api.userApi.apiUserPost(input.userUserIO);
      return res.data as UserUserIO;
    },
    onMutate: async ({ userUserIO }) => {
      const username = userUserIO.username;

      await Promise.all([
        qc.cancelQueries({ queryKey: queryKeys.users.detail(username) }),
        qc.cancelQueries({ queryKey: queryKeys.users.all }),
      ]);

      const previousDetail = qc.getQueryData<UserUserIO>(queryKeys.users.detail(username));
      const previousList = qc.getQueryData<UserUserIO[]>(queryKeys.users.all);

      if (previousDetail) {
        qc.setQueryData<UserUserIO>(queryKeys.users.detail(username), {
          ...previousDetail,
          ...userUserIO,
        });
      } else {
        qc.setQueryData<UserUserIO>(queryKeys.users.detail(username), userUserIO);
      }

      if (previousList) {
        qc.setQueryData<UserUserIO[]>(
          queryKeys.users.all,
          previousList.map((u) => (u.username === username ? { ...u, ...userUserIO } : u))
        );
      }

      return { previousDetail, previousList };
    },
    onError: (_err, variables, context) => {
      const username = variables.userUserIO.username;

      if (context?.previousDetail) {
        qc.setQueryData(queryKeys.users.detail(username), context.previousDetail);
      }

      if (context?.previousList) {
        qc.setQueryData(queryKeys.users.all, context.previousList);
      }
    },
    onSuccess: (savedUser) => {
      const username = savedUser.username;

      qc.setQueryData(queryKeys.users.detail(username), savedUser);

      qc.setQueryData<UserUserIO[]>(queryKeys.users.all, (old) => {
        if (!old) return old;
        return old.map((u) => (u.username === username ? savedUser : u));
      });
    },
    onSettled: (_data, _err, variables) => {
      const username = variables.userUserIO.username;

      void qc.invalidateQueries({ queryKey: queryKeys.users.detail(username) });
      void qc.invalidateQueries({ queryKey: queryKeys.users.all });
    },
    meta: {
      skipGlobalError: false,
    },
  });
}
